import { type ReactNode } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-teal-700 to-teal-600 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-transparent pointer-events-none" />
        <div className="absolute -right-20 -top-20 w-96 h-96 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -left-10 bottom-0 w-64 h-64 bg-teal-500/30 rounded-full pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 px-4 py-1.5 rounded-full text-sm mb-5">
            <i className="ri-file-text-line"></i>
            <span>藍舍AI地產 · 使用規範</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">使用條款</h1>
          <p className="text-white/80 text-base max-w-xl mx-auto">
            請詳閱以下條款，使用本平台即代表您同意遵守所有規定。
          </p>
          <p className="text-white/60 text-sm mt-4">最後更新日期：2026年03月18日</p>
        </div>
      </section>

      {/* Beta 測試版聲明 */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-start gap-3">
          <i className="ri-information-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>測試版公告：</strong>本平台目前處於測試（Beta）階段，所有條款及服務內容均為試營運性質，可能隨時調整並另行公告。本平台不對因服務異常、功能變更或系統中斷所造成的任何損失負責。
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-14">

        {/* 提醒 Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-10 flex items-start gap-3">
          <i className="ri-alert-line text-amber-500 mt-0.5 flex-shrink-0 text-lg"></i>
          <p className="text-sm text-amber-800">
            <strong>重要提醒：</strong>使用藍舍AI地產平台服務，即表示您已閱讀、理解並同意以下所有使用條款。若您不同意任何條款，請停止使用本平台。
          </p>
        </div>

        {/* 目錄 */}
        <nav className="bg-white rounded-xl p-6 mb-10 border border-gray-100" aria-label="目錄">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">目錄</h2>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              ['1', '服務說明與範圍'],
              ['2', '帳號註冊與使用'],
              ['3', '用戶行為準則'],
              ['4', '房源發布規範'],
              ['5', '智慧財產權'],
              ['6', '免責聲明'],
              ['7', '服務中斷與終止'],
              ['8', '爭議解決'],
            ].map(([num, label]) => (
              <li key={num}>
                <a
                  href={`#term-${num}`}
                  className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 transition-colors cursor-pointer"
                >
                  <span className="w-6 h-6 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center text-xs font-bold flex-shrink-0">{num}</span>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-10">

          <TermSection id="term-1" icon="ri-service-line" title="1. 服務說明與範圍">
            <p>「藍舍AI地產」（以下簡稱「本平台」）是一個結合人工智能技術的免仲介租屋媒合平台，提供以下服務：</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {[
                { icon: 'ri-home-4-line', text: '房源發布與管理服務' },
                { icon: 'ri-search-eye-line', text: '智能房源搜尋與篩選' },
                { icon: 'ri-sparkling-2-line', text: 'AI 房源媒合與推薦' },
                { icon: 'ri-message-3-line', text: '房東與租客站內通訊' },
                { icon: 'ri-bar-chart-2-line', text: '房地產市場行情分析' },
                { icon: 'ri-shield-user-line', text: '租客背景篩選輔助' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <i className={`${item.icon} text-teal-500 flex-shrink-0`}></i>
                  <span className="text-sm text-gray-700">{item.text}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-gray-500 text-xs">本平台保留隨時修改、暫停或終止任何服務的權利，並將盡合理努力提前通知用戶。</p>
          </TermSection>

          <TermSection id="term-2" icon="ri-user-add-line" title="2. 帳號註冊與使用">
            <p>使用本平台完整功能需完成帳號註冊，您同意：</p>
            <ul className="space-y-3 mt-4">
              {[
                { title: '提供真實資訊', desc: '所提供的姓名、聯絡方式及相關資訊均為真實、準確且最新' },
                { title: '保管帳號安全', desc: '自行負責保管帳號密碼，不得將帳號轉讓或借予他人使用' },
                { title: '及時更新資料', desc: '個人資訊有變更時，應即時於帳號設定中進行更新' },
                { title: '通報異常使用', desc: '若發現帳號遭未授權使用，應立即通知本平台客服' },
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 bg-white border border-gray-100 rounded-lg p-4">
                  <span className="w-7 h-7 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                  <div>
                    <strong className="text-gray-800 text-sm">{item.title}</strong>
                    <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </TermSection>

          <TermSection id="term-3" icon="ri-honor-line" title="3. 用戶行為準則">
            <p>使用本平台時，您<strong>嚴格禁止</strong>以下行為：</p>
            <div className="mt-4 space-y-2">
              {[
                '發布虛假、誤導性或詐騙性的房源資訊',
                '騷擾、威脅、歧視或侵害其他用戶',
                '未經授權蒐集或使用其他用戶的個人資料',
                '上傳含有病毒、惡意程式或有害內容',
                '操控評價或使用機器人程式進行自動化操作',
                '進行任何違反中華民國法律法規的行為',
                '使用本平台進行與租屋無關的商業推廣',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-red-50 rounded-lg p-3">
                  <i className="ri-close-circle-line text-red-400 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-gray-600">違反上述規定者，本平台有權立即暫停或終止其帳號，並保留追究法律責任的權利。</p>
          </TermSection>

          <TermSection id="term-4" icon="ri-home-gear-line" title="4. 房源發布規範">
            <p>房東發布房源時，必須確保：</p>
            <ul className="list-disc pl-5 space-y-2 mt-3 text-sm text-gray-700">
              <li>您對該房源具有合法的出租權利或授權</li>
              <li>房源資訊（地址、格局、面積、租金）真實準確</li>
              <li>上傳的照片為實際房源狀況，不得使用他人照片或 AI 生成虛假圖片</li>
              <li>租金與費用說明清楚透明，不得有隱藏費用</li>
              <li>房源符合居住安全標準，不得出租不符規定的房間</li>
            </ul>
            <div className="bg-teal-50 rounded-xl p-4 mt-5 flex items-start gap-3">
              <i className="ri-information-line text-teal-500 mt-0.5 flex-shrink-0"></i>
              <p className="text-sm text-gray-700">本平台保留審核、修改或下架不符規定房源的權利，違規發布可能導致帳號停用。</p>
            </div>
          </TermSection>

          <TermSection id="term-5" icon="ri-copyright-line" title="5. 智慧財產權">
            <p>關於本平台的智慧財產權：</p>
            <div className="space-y-4 mt-4">
              <div className="border-l-4 border-teal-400 pl-4">
                <h4 className="font-semibold text-gray-800 text-sm mb-1">平台內容</h4>
                <p className="text-sm text-gray-600">本平台的設計、程式碼、商標、標誌、文字及其他內容，均受著作權法及相關法律保護，未經授權不得複製、散佈或使用。</p>
              </div>
              <div className="border-l-4 border-teal-400 pl-4">
                <h4 className="font-semibold text-gray-800 text-sm mb-1">用戶內容</h4>
                <p className="text-sm text-gray-600">您發布在本平台的內容（房源照片、描述等）著作權歸您所有，但您授予本平台非專屬、免費的使用授權，用於提供平台服務。</p>
              </div>
              <div className="border-l-4 border-teal-400 pl-4">
                <h4 className="font-semibold text-gray-800 text-sm mb-1">AI 生成內容</h4>
                <p className="text-sm text-gray-600">由本平台 AI 系統生成的房源描述、分析報告等內容，版權歸本平台所有，用戶可於本平台範圍內使用。</p>
              </div>
            </div>
          </TermSection>

          <TermSection id="term-6" icon="ri-error-warning-line" title="6. 免責聲明">
            <p>本平台提供中介媒合服務，以下事項本平台不承擔責任：</p>
            <ul className="space-y-2 mt-4">
              {[
                '用戶提供虛假房源或個人資訊所造成的損失',
                '房東與租客之間的租賃糾紛及財務損失',
                '因不可抗力或第三方原因導致的服務中斷',
                'AI 系統分析結果的絕對準確性，建議用戶自行驗證',
                '因用戶違反使用條款而導致的帳號處置',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                  <i className="ri-error-warning-line text-amber-400 flex-shrink-0 mt-0.5"></i>
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </TermSection>

          <TermSection id="term-7" icon="ri-shut-down-line" title="7. 服務中斷與終止">
            <p>以下情況可能導致服務中斷或帳號終止：</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {[
                { title: '系統維護', desc: '定期維護期間可能暫時停止服務，將提前公告' },
                { title: '違規行為', desc: '違反使用條款者，帳號可能被立即停用' },
                { title: '安全威脅', desc: '偵測到安全威脅時，可能暫時限制帳號存取' },
                { title: '用戶申請', desc: '您可隨時申請刪除帳號，資料將依隱私政策處理' },
              ].map((item, i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">{item.title}</h4>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </TermSection>

          <TermSection id="term-8" icon="ri-scales-3-line" title="8. 爭議解決">
            <p>關於本使用條款的解釋及因使用本平台產生的爭議：</p>
            <ul className="list-disc pl-5 space-y-2 mt-3 text-sm text-gray-700">
              <li>本條款依據<strong>中華民國法律</strong>解釋及適用</li>
              <li>雙方應優先以友好協商方式解決爭議</li>
              <li>協商未果，同意以<strong>台灣台北地方法院</strong>為第一審管轄法院</li>
              <li>本平台提供客服管道協助處理投訴與爭議</li>
            </ul>
            <div className="bg-teal-50 rounded-xl p-4 mt-5 flex items-start gap-3">
              <i className="ri-customer-service-2-line text-teal-500 mt-0.5 flex-shrink-0"></i>
              <p className="text-sm text-gray-700">如有任何問題或投訴，請聯絡：<a href="mailto:info@ailanshe.com" className="text-teal-600 font-medium hover:underline">info@ailanshe.com</a></p>
            </div>
          </TermSection>

        </div>

        {/* Agreement Footer */}
        <div className="mt-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 text-white text-center">
          <i className="ri-file-text-line text-4xl mb-3 block"></i>
          <h3 className="text-xl font-bold mb-2">感謝您閱讀使用條款</h3>
          <p className="text-white/80 text-sm mb-5">如有任何疑問，歡迎隨時聯絡我們的服務團隊</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a
              href="/privacy-policy"
              className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-5 py-2.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shield-check-line"></i>
              查看隱私政策
            </a>
            <a
              href="mailto:info@ailanshe.com"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-5 py-2.5 rounded-full font-semibold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              聯絡我們
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function TermSection({ id, icon, title, children }: { id: string; icon: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="bg-white rounded-xl p-7 border border-gray-100">
      <h2 className="flex items-center gap-2.5 text-lg font-bold text-gray-900 mb-5 pb-4 border-b border-gray-100">
        <span className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
          <i className={`${icon} text-teal-500 text-lg`}></i>
        </span>
        {title}
      </h2>
      <div className="text-gray-600 text-sm leading-relaxed space-y-2">{children}</div>
    </section>
  );
}
